<template>
  <div class="">
    <Alert v-if="showErrorModal" variant="destructive" class="mb-4">
      <AlertDescription>{{ errorMessage }}</AlertDescription>
    </Alert>
    <Alert v-if="showSuccessModal" class="mb-4">
      <AlertDescription>Password updated</AlertDescription>
    </Alert>
    <form class="space-y-4" @submit.prevent>
      <!-- title -->
      <div class="text-center">
        <h1 class="text-xl font-medium">Create new password</h1>
        <h1 class="text-sm sm:text-md font-light pt-1 pb-3">Enter and confirm your new password</h1>
      </div>

      <!-- password input -->
      <Input
        v-model="passwordResetRequest.password"
        id="newPassword"
        placeholder="New password"
        name="password"
        type="password"
      />

      <!-- confirm password input -->
      <Input
        v-model="confirmPassword"
        id="confirmPassword"
        placeholder="Confirm password"
        name="password"
        type="password"
      />

      <Button
        type="submit"
        id="button"
        :disabled="!secure || !matching"
        @click="resetPassword"
        class="w-full h-10"
      >
        Confirm and sign in
      </Button>

      <div class="text-muted-foreground text-sm">
        <h2 class="font-medium">Password requirements</h2>
        <p>Please follow this guide for a strong password</p>
        <p :class="emailStrong.special ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One special character
        </p>
        <p :class="emailStrong.length ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - Min 8 characters
        </p>
        <p :class="emailStrong.number ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One number (2 recommended)
        </p>
        <p :class="emailStrong.capital ? 'text-blue-600 font-medium' : 'text-muted-foreground'">
          - One capital
        </p>
        <p :class="matching ? 'text-blue-600 font-medium' : 'text-muted-foreground'">- Matching</p>
      </div>
    </form>
  </div>
</template>

<script>
import { Input, Button, Alert, AlertDescription } from '@boogi/ui'

export default {
  name: 'ResetPassword',
  components: {
    Input,
    Button,
    Alert,
    AlertDescription,
  },
  props: {
    token: {
      type: String,
    },
    email: {
      type: String,
    },
  },
  data() {
    return {
      confirmPassword: undefined,
      passwordResetRequest: {
        id: this.$route.params.id,
        password: undefined,
      },
      errors: {},
      errorMessage: '',
      showErrorModal: false,
      loading: false,
      showSuccessModal: false,

      emailStrong: {
        special: false,
        length: false,
        number: false,
        capital: false,
      },
    }
  },
  methods: {
    async resetPassword() {
      this.showErrorModal = false
      this.loading = true
      try {
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/auth/reset-password`, {
          method: 'POST',
          body: JSON.stringify(this.passwordResetRequest),
          headers: { 'Content-Type': 'application/json' },
        })

        if (response && response.ok) {
          this.showSuccessModal = true
          // The link is single-use and the user now has a new password, so send them
          // to sign in (the button promises exactly this).
          setTimeout(() => this.$router.push('/auth/sign-in'), 1500)
        } else {
          this.showErrorModal = true
          this.errorMessage =
            'This reset link is invalid or has expired. Please request a new one.'
        }
      } catch (error) {
        this.showErrorModal = true
        this.errorMessage = 'Something went wrong. Please try again.'
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    passwordResetRequest: {
      deep: true,
      handler: function (val) {
        const password = val.password || ''
        if (password.match(/^(?=.*[A-Z])/)) {
          this.emailStrong.capital = true
        } else {
          this.emailStrong.capital = false
        }
        if (password.match(/^(?=.*[0-9])/)) {
          this.emailStrong.number = true
        } else {
          this.emailStrong.number = false
        }
        if (password.match(/^(?=.*[!@#$%^&*-])/)) {
          this.emailStrong.special = true
        } else {
          this.emailStrong.special = false
        }
        if (password.match(/^(?=.{8,})/)) {
          this.emailStrong.length = true
        } else {
          this.emailStrong.length = false
        }
      },
    },
  },

  computed: {
    matching() {
      return (
        this.confirmPassword === this.passwordResetRequest.password &&
        this.passwordResetRequest.password != ''
      )
    },
    secure() {
      return (
        this.emailStrong.length &&
        this.emailStrong.special &&
        this.emailStrong.number &&
        this.emailStrong.capital
      )
    },
  },
  mounted() {
    document.title = 'Reset password'
  },
}
</script>

<style></style>
